import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import type { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import '@wangeditor/editor/dist/css/style.css';
import { uploadFile, normalizeFileUrl } from '../services/fileService';

interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  height?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  placeholder = '请输入内容',
  height = 320,
}) => {
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const [html, setHtml] = useState(value);

  useEffect(() => {
    setHtml(value || '');
  }, [value]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  const toolbarConfig = useMemo<Partial<IToolbarConfig>>(
    () => ({
      toolbarKeys: [
        'headerSelect',
        'bold',
        'italic',
        'underline',
        'through',
        '|',
        'color',
        'bgColor',
        '|',
        'bulletedList',
        'numberedList',
        'blockquote',
        '|',
        'justifyLeft',
        'justifyCenter',
        'justifyRight',
        '|',
        'insertLink',
        'uploadImage',
        'undo',
        'redo',
      ],
    }),
    []
  );

  const editorConfig = useMemo<Partial<IEditorConfig>>(
    () => ({
      placeholder,
      MENU_CONF: {
        uploadImage: {
          async customUpload(file: File, insertFn: (url: string, alt?: string, href?: string) => void) {
            try {
              const res = await uploadFile(file, 'mini_program_user_richtext');
              const imageUrl = normalizeFileUrl(res.data.url);
              insertFn(imageUrl, file.name, imageUrl);
            } catch (error: any) {
              message.error(error?.response?.data?.message || '图片上传失败');
            }
          },
        },
      },
    }),
    [placeholder]
  );

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
      <Toolbar editor={editor} defaultConfig={toolbarConfig} mode="default" />
      <Editor
        defaultConfig={editorConfig}
        value={html}
        mode="default"
        onCreated={setEditor}
        onChange={(currentEditor) => {
          const nextHtml = currentEditor.getHtml();
          setHtml(nextHtml);
          onChange?.(nextHtml);
        }}
        style={{ height, overflowY: 'hidden' }}
      />
    </div>
  );
};

export default RichTextEditor;
